import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  INVENTORY_STAGE_LABELS,
  INVENTORY_STAGES,
  isShopStage,
  isVenueStage,
  roleCanInventory,
  SESSION_STATUS_LABELS,
  type InventorySessionListResponse,
  type InventorySessionResponse,
  type InventoryStage,
  type QrScan,
} from '@inventohub/shared';
import { HelpLink } from '../components/HelpLink';
import { QrScanPanel } from '../components/QrScanPanel';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import { lookupErrorMessage, resolveQrScan } from '../lib/qr';

export function InventoryStartPage(): JSX.Element {
  const { user, code } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toteNumber = params.get('tote') ?? '';
  const presetLoadout = params.get('loadout') ?? '';
  const presetStage = params.get('stage') ?? '';
  const canStart = Boolean(code || (user && roleCanInventory(user.role)));
  const [lookup, setLookup] = useState(toteNumber);
  const [detail, setDetail] = useState<InventorySessionListResponse | null>(null);
  const allowedStages = code
    ? INVENTORY_STAGES.filter((entry) => isVenueStage(entry))
    : INVENTORY_STAGES;
  const [stage, setStage] = useState<InventoryStage>(
    allowedStages.includes(presetStage as InventoryStage)
      ? (presetStage as InventoryStage)
      : (allowedStages[0] ?? 'shop_pack'),
  );
  const [loadoutId, setLoadoutId] = useState(presetLoadout || code?.loadoutId || '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadouts = detail?.loadouts ?? [];
  const shopGeneralAllowed = isShopStage(stage);
  const venueNeedsLoadout = isVenueStage(stage);
  const selectedLoadout = loadouts.find((row) => row.id === loadoutId) ?? null;

  useEffect(() => {
    const number = toteNumber.trim();
    if (!number) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<InventorySessionListResponse>(
          `/api/inventory/sessions?tote=${encodeURIComponent(number)}`,
        );
        if (cancelled) return;
        setDetail(body);
        setLoadoutId((current) => current || presetLoadout || current);
      } catch (caught: unknown) {
        if (cancelled) return;
        setDetail(null);
        setError(caught instanceof ApiClientError ? caught.message : 'Could not load that tote.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toteNumber, presetLoadout]);

  async function loadTote(number: string): Promise<void> {
    const body = await apiRequest<InventorySessionListResponse>(
      `/api/inventory/sessions?tote=${encodeURIComponent(number)}`,
    );
    setDetail(body);
  }

  async function onScan(scan: QrScan): Promise<void> {
    setError(null);
    try {
      if (scan.kind === 'asset') {
        setDetail(null);
        setError('That QR is an asset. Type or scan a tote to start inventory.');
        return;
      }
      const found = await resolveQrScan(scan);
      if (!found) {
        setDetail(null);
        setError('No tote matches that QR or typed number.');
        return;
      }
      if (found.kind !== 'tote') {
        setDetail(null);
        setError('That QR is an asset. Type or scan a tote to start inventory.');
        return;
      }
      setLookup(found.tote.number);
      await loadTote(found.tote.number);
    } catch (caught: unknown) {
      setDetail(null);
      setError(lookupErrorMessage(caught, 'Could not load that tote.'));
    }
  }

  async function onStart(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!detail || !canStart) return;
    setBusy(true);
    setError(null);
    try {
      const body = await apiRequest<InventorySessionResponse>('/api/inventory/sessions', {
        method: 'POST',
        body: {
          toteId: detail.tote.id,
          stage,
          loadoutId: loadoutId || null,
        },
      });
      await navigate(`/inventory/${body.session.id}`);
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not open an inventory session.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="inventory-start">
      <div className="card">
        <p className="meta">
          <Link to={detail ? `/totes/${encodeURIComponent(detail.tote.number)}` : '/totes'}>
            Back to tote
          </Link>
        </p>
        <div className="card__title-row">
          <h2>Inventory a tote</h2>
          <HelpLink slug="inventory" />
        </div>
        <p className="meta">Submit is per tote. Choose a loadout and stage, then mark each item.</p>
        {error ? (
          <p className="status status--error" data-testid="inventory-start-error">
            {error}
          </p>
        ) : null}
        {detail?.lock?.held && !detail.lock.youHoldIt ? (
          <p className="status status--warn" data-testid="lock-holder">
            {detail.lock.holder?.name ?? 'Someone'} is inventorying this tote.
          </p>
        ) : null}
      </div>

      <div className="card">
        <QrScanPanel
          label="Tote number or QR"
          value={lookup}
          onValueChange={setLookup}
          onScan={(scan) => void onScan(scan)}
          submitLabel="Look up tote"
          inputTestId="inventory-tote"
        />
      </div>

      {detail ? (
        <form className="card" onSubmit={(event) => void onStart(event)}>
          <p>
            Tote {detail.tote.number}
            {detail.tote.notes ? ` — ${detail.tote.notes}` : ''}
          </p>
          <label className="field">
            Stage
            <select
              value={stage}
              onChange={(event) => {
                const next = event.target.value as InventoryStage;
                setStage(next);
                if (isShopStage(next) && !presetLoadout) setLoadoutId('');
              }}
              data-testid="inventory-stage"
            >
              {allowedStages.map((value) => (
                <option key={value} value={value}>
                  {INVENTORY_STAGE_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Loadout
            <select
              value={loadoutId}
              onChange={(event) => setLoadoutId(event.target.value)}
              required={venueNeedsLoadout}
              data-testid="inventory-loadout"
            >
              {shopGeneralAllowed ? <option value="">Shop general (no loadout)</option> : null}
              {loadouts.map((loadout) => (
                <option key={loadout.id} value={loadout.id}>
                  {loadout.events.map((event) => event.name).join(' + ') ||
                    loadout.name ||
                    'Unnamed loadout'}
                </option>
              ))}
            </select>
          </label>
          {venueNeedsLoadout && loadouts.length === 0 ? (
            <p className="status status--warn">
              This tote is not on a loadout, so venue receive / pack-out is not available. Use shop
              pack or shop receive, or assign it on the event page.
            </p>
          ) : null}
          {selectedLoadout ? (
            <p className="meta">
              {selectedLoadout.events
                .map((event) => `${event.name} (${event.startDate}–${event.endDate})`)
                .join(' · ') || 'No events on this loadout yet.'}
            </p>
          ) : null}
          {canStart ? (
            <button
              type="submit"
              className="button"
              disabled={busy || (venueNeedsLoadout && !loadoutId)}
              data-testid="open-inventory"
            >
              {busy ? 'Opening…' : 'Open session'}
            </button>
          ) : (
            <p className="meta">Viewers can look at inventory but cannot start a session.</p>
          )}
        </form>
      ) : null}

      {detail && detail.sessions.length > 0 ? (
        <div className="card">
          <h2>Existing sessions</h2>
          <ul className="link-list">
            {detail.sessions.map((session) => (
              <li key={session.id}>
                <Link to={`/inventory/${session.id}`} data-testid={`resume-${session.stage}`}>
                  {INVENTORY_STAGE_LABELS[session.stage]} · {SESSION_STATUS_LABELS[session.status]}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
